import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, IconButton, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PresupuestoFormDialog from "./PresupuestoFormDialog";
import PresupuestoImportDialog from "./PresupuestoImportDialog";
import PresupuestoComparacion from "./PresupuestoComparacion";
import { getCatalogosEgresos } from "../services/egresosService";
import { buscarPresupuestos, eliminarPresupuesto, fmt, MESES, num } from "../services/presupuestoService";

const fieldSx = {
  "& .MuiInputBase-root": { minHeight: 32 },
  "& .MuiInputBase-input": { py: "5px", fontSize: "0.8rem" },
  "& .MuiInputLabel-root": { fontSize: "0.8rem" },
};
const cellSx = { py: 0.2, px: 0.5, fontSize: 11 };
const obs = (r) => r.Observacion ?? r.observacion ?? "";

const COLUMNAS = [
  { k: "nombre", etq: "Nombre", tipo: "texto" }, { k: "concepto", etq: "Concepto", tipo: "texto" },
  ...MESES.map((m) => ({ k: m, etq: m, tipo: "num" })),
  { k: "id_macrocuenta", etq: "Macrocuenta", tipo: "num" }, { k: "cuenta", etq: "Cuenta", tipo: "texto" },
  { k: "nro_contrato", etq: "Contrato", tipo: "texto" }, { k: "moneda", etq: "Moneda", tipo: "texto" },
];

// Presupuestos por año (espejo de presupuesto/index.php y presupuestos_resultados.php): consulta con filtros y orden,
// alta, edición, baja, exportación a CSV, importación masiva y comparación contra lo ejecutado.
export default function PresupuestoTab() {
  const [vista, setVista] = useState("presupuestos");
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [anioInput, setAnioInput] = useState(String(new Date().getFullYear()));
  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [texto, setTexto] = useState("");
  const [moneda, setMoneda] = useState("");
  const [concepto, setConcepto] = useState("");
  const [orden, setOrden] = useState({ k: null, dir: 1 });
  const [catalogos, setCatalogos] = useState(null);
  const [formAbierto, setFormAbierto] = useState(false);
  const [filaEditar, setFilaEditar] = useState(null);
  const [importAbierto, setImportAbierto] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError("");
    try {
      setDatos(await buscarPresupuestos(anio));
    } catch {
      setDatos([]);
      setError("No se pudo cargar los presupuestos.");
    } finally {
      setCargando(false);
    }
  }, [anio]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { getCatalogosEgresos().then((r) => setCatalogos(r.data)).catch(() => setCatalogos({ conceptos: [], nombres: [] })); }, []);

  const buscarAnio = () => {
    const a = Number(anioInput);
    const max = new Date().getFullYear() + 1;
    if (!Number.isInteger(a) || a < 2000 || a > max) { setError(`El año debe estar entre 2000 y ${max}.`); return; }
    setAnio(a);
  };

  const monedas = useMemo(() => [...new Set(datos.map((r) => r.moneda).filter(Boolean))].sort(), [datos]);
  const conceptos = useMemo(() => [...new Set(datos.map((r) => r.concepto).filter(Boolean))].sort((a, b) => a.localeCompare(b, "es")), [datos]);

  const filas = useMemo(() => {
    const t = texto.trim().toLowerCase();
    let r = datos.filter((x) => {
      if (moneda && x.moneda !== moneda) return false;
      if (concepto && x.concepto !== concepto) return false;
      if (!t) return true;
      return [x.nombre, x.concepto, x.cuenta, x.nro_contrato, obs(x)].join(" ").toLowerCase().includes(t);
    });
    if (orden.k) {
      const col = COLUMNAS.find((c) => c.k === orden.k);
      r = [...r].sort((a, b) => (col.tipo === "num"
        ? (num(a[orden.k]) - num(b[orden.k]))
        : String(a[orden.k] ?? "").localeCompare(String(b[orden.k] ?? ""), "es", { numeric: true, sensitivity: "base" })) * orden.dir);
    }
    return r;
  }, [datos, texto, moneda, concepto, orden]);

  const totales = useMemo(() => Object.fromEntries(MESES.map((m) => [m, filas.reduce((a, r) => a + num(r[m]), 0)])), [filas]);

  const eliminar = async (r) => {
    if (!window.confirm(`¿Eliminar el presupuesto de "${r.nombre}" (${r.concepto})?`)) return;
    try {
      await eliminarPresupuesto(r.id);
      setOk("Presupuesto eliminado.");
      cargar();
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo eliminar.");
    }
  };

  const exportarCsv = () => {
    const cab = [...COLUMNAS.map((c) => c.etq), "Observación"];
    const cuerpo = filas.map((r) => [...COLUMNAS.map((c) => (c.tipo === "num" && MESES.includes(c.k) ? fmt(r[c.k]) : r[c.k] ?? "")), obs(r)]);
    const lineas = [cab, ...cuerpo].map((f) => f.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
    const url = URL.createObjectURL(new Blob([`﻿${lineas.join("\n")}`], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url; a.download = `presupuestos_${anio}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 1, mb: 1, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
        <ToggleButtonGroup size="small" exclusive value={vista} onChange={(_, v) => v && setVista(v)} sx={{ "& .MuiToggleButton-root": { py: 0.2, px: 1.2, fontSize: 12, textTransform: "none", fontWeight: 700 } }}>
          <ToggleButton value="presupuestos">Presupuestos</ToggleButton>
          <ToggleButton value="comparacion">Comparar con egresos</ToggleButton>
        </ToggleButtonGroup>
        <TextField size="small" type="number" label="Año" value={anioInput} onChange={(e) => setAnioInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") buscarAnio(); }} sx={{ ...fieldSx, width: 100 }} />
        <Button size="small" variant="contained" onClick={buscarAnio}>Buscar</Button>
        <Box sx={{ flexGrow: 1 }} />
        {vista === "presupuestos" && (
          <>
            <Button size="small" variant="contained" color="success" disabled={!catalogos} onClick={() => { setFilaEditar(null); setFormAbierto(true); }}>Adicionar</Button>
            <Button size="small" variant="outlined" onClick={() => setImportAbierto(true)}>Importar</Button>
            <Button size="small" variant="outlined" disabled={!filas.length} onClick={exportarCsv}>Exportar CSV</Button>
          </>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 1, py: 0 }} onClose={() => setError("")}>{error}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 1, py: 0 }} onClose={() => setOk("")}>{ok}</Alert>}

      {vista === "comparacion" && <PresupuestoComparacion anio={anio} />}

      {vista === "presupuestos" && (
        <>
          <Paper variant="outlined" sx={{ p: 1, mb: 1, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <TextField size="small" label="Búsqueda" placeholder="Nombre, concepto, cuenta, contrato, observación..." value={texto} onChange={(e) => setTexto(e.target.value)} sx={{ ...fieldSx, width: 320 }} />
            <TextField select size="small" label="Moneda" value={moneda} onChange={(e) => setMoneda(e.target.value)} sx={{ ...fieldSx, width: 140 }}>
              <MenuItem value="">Todas</MenuItem>
              {monedas.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Concepto" value={concepto} onChange={(e) => setConcepto(e.target.value)} sx={{ ...fieldSx, width: 240 }}>
              <MenuItem value="">Todos</MenuItem>
              {conceptos.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <Button size="small" onClick={() => { setTexto(""); setMoneda(""); setConcepto(""); }}>Limpiar filtros</Button>
            {cargando && <CircularProgress size={16} />}
          </Paper>

          <Paper variant="outlined" sx={{ overflow: "hidden" }}>
            <Box sx={{ px: 1, py: 0.5, bgcolor: "action.hover" }}>
              <Typography sx={{ fontSize: 12, fontWeight: 800 }}>Presupuestos — Año {anio} ({filas.length})</Typography>
            </Box>
            <TableContainer sx={{ maxHeight: "66vh" }}>
              <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": cellSx }}>
                <TableHead>
                  <TableRow>
                    {COLUMNAS.map((c) => (
                      <TableCell key={c.k} align={c.tipo === "num" && MESES.includes(c.k) ? "right" : "left"} sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>
                        <TableSortLabel active={orden.k === c.k} direction={orden.dir === 1 ? "asc" : "desc"}
                          onClick={() => setOrden((o) => ({ k: c.k, dir: o.k === c.k ? -o.dir : 1 }))}>{c.etq}</TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell sx={{ fontWeight: 800 }}>Observación</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!filas.length ? (
                    <TableRow><TableCell colSpan={COLUMNAS.length + 2}><Typography variant="body2" color="text.secondary">{cargando ? "Cargando..." : `No se encontraron presupuestos para el año ${anio}.`}</Typography></TableCell></TableRow>
                  ) : filas.map((r) => (
                    <TableRow key={r.id} hover>
                      {COLUMNAS.map((c) => (
                        <TableCell key={c.k} align={MESES.includes(c.k) ? "right" : "left"}
                          sx={{ whiteSpace: "nowrap", ...(c.k === "nombre" || c.k === "concepto" ? { maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" } : {}) }}
                          title={c.k === "nombre" || c.k === "concepto" ? r[c.k] : undefined}>
                          {MESES.includes(c.k) ? fmt(r[c.k]) : r[c.k]}
                        </TableCell>
                      ))}
                      <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={obs(r)}>{obs(r)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Tooltip title="Editar"><IconButton size="small" color="primary" sx={{ p: 0.3 }} onClick={() => { setFilaEditar(r); setFormAbierto(true); }}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                        <Tooltip title="Eliminar"><IconButton size="small" color="error" sx={{ p: 0.3 }} onClick={() => eliminar(r)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filas.length > 0 && (
                    <TableRow sx={{ bgcolor: "action.hover" }}>
                      <TableCell colSpan={2} align="right" sx={{ fontWeight: 800 }}>Totales:</TableCell>
                      {MESES.map((m) => <TableCell key={m} align="right" sx={{ fontWeight: 800 }}>{fmt(totales[m])}</TableCell>)}
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {catalogos && (
        <PresupuestoFormDialog abierto={formAbierto} fila={filaEditar} anio={anio} catalogos={catalogos} onClose={() => setFormAbierto(false)}
          onGuardado={(m) => { setOk(m); cargar(); }} />
      )}
      <PresupuestoImportDialog abierto={importAbierto} onClose={() => setImportAbierto(false)} onImportado={() => { setOk("Presupuestos importados."); cargar(); }} />
    </Box>
  );
}
